import { useQuery } from '@tanstack/react-query';

import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import { TSegment } from '@/features/segments/types';
import { mediaManagerClient } from '@/lib/axios';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';

type TGetBroadcastScheduleSegmentsFilters = {
  id: string;
  filters?: Partial<ProgramBroadcastFiltersState>;
};

export async function getBroadcastScheduleSegmentById({
  id,
  filters,
}: TGetBroadcastScheduleSegmentsFilters): Promise<TSegment[]> {
  return mediaManagerClient
    .get(`api/v1/program_broadcasts/${id}/segments`, {
      params: {
        ...(filters?.search && { search: filters.search }),
        // Sin acotar por fechas a propósito (RCOMM-5948). El endpoint ya selecciona los
        // segmentos que solapan la franja a partir del program_broadcast de la ruta. Reenviar
        // start_datetime__gte/end_datetime__lte se combina en AND con ese solape y lo degrada a
        // contención: como las grabaciones son bloques de 2 min con rejilla propia, se caen el
        // bloque que abre la franja y el que la cierra, ambos marcados como contenido del
        // programa en parameters.program_values.
        ...(filters?.review_states &&
          filters.review_states.length > 0 && {
            review_states: filters.review_states.join(','),
          }),
        ...(filters?.logos &&
          filters?.logos.length > 0 && { logo_id__in: filters.logos.join(',') }),
        ...(filters?.programs &&
          filters?.programs.length > 0 && {
            program_id__in: filters.programs.join(','),
          }),
        ...(filters?.people &&
          filters?.people.length > 0 && {
            people_id__in: filters.people.join(','),
          }),
        ...(filters?.workspaces &&
          filters?.workspaces.length > 0 && {
            workspace_id__in: filters.workspaces.join(','),
          }),
        ...(filters?.tags &&
          filters?.tags.length > 0 && {
            tag_id__in: filters.tags.join(','),
          }),
        ...(filters?.order_by && { order_by: filters.order_by }),
      },
    })
    .then(response => response.data)
    .catch(error => {
      console.error(error);
      return [];
    });
}

type QueryFnType = typeof getBroadcastScheduleSegmentById;

interface UseResourcesParams {
  id?: string;
  config?: QueryConfig<QueryFnType>;
  filters?: Partial<ProgramBroadcastFiltersState>;
}

export function useGetBroadcastScheduleSegmentById({
  id = '',
  config,
  filters,
}: UseResourcesParams = {}) {
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    enabled: !!id,
    queryKey: broadcastScheduleQueryKeys.segments(id, filters),
    queryFn: () =>
      getBroadcastScheduleSegmentById({
        id,
        filters,
      }),
  });
}
